import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiPlus, FiSearch, FiArrowRight, FiPrinter, FiX,
  FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight,
  FiArchive, FiPackage, FiRepeat
} from 'react-icons/fi';
import {
  getTransfers, createTransfer, anularTransfer,
  getWarehouses, getWarehouseStock
} from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import '../../../styles/ModuleTable.css';
import './TransferenciasMaintenance.css';

/* ── Helpers ─────────────────────────────────────────────────────────── */
function fmtDt(str) {
  if (!str) return '-';
  const d = new Date(str);
  const p = n => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth()+1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
function fmtQ(n, decimals = 3) {
  const v = parseFloat(n ?? 0).toFixed(decimals);
  return v.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

const ITEMS = [10, 20, 50];

/* ── Impresión de comprobante ─────────────────────────────────────────── */
function imprimirTransferencia(t, businessName = 'PIVASAPOLLOS') {
  const win = window.open('', '_blank', 'width=700,height=600');
  const fecha = fmtDt(t.created_at);
  const especial = !!t.special_condition;

  win.document.write(`<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>Transferencia ${t.transfer_number}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #1e293b; padding: 24px 32px; }
    .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #0d1b2e; padding-bottom: 14px; }
    .co-name { font-size: 18px; font-weight: 800; color: #0d1b2e; letter-spacing: 1px; }
    .doc-title { font-size: 13px; font-weight: 700; color: #7c3aed; margin-top: 6px; letter-spacing: 2px; text-transform: uppercase; }
    .doc-number { font-size: 22px; font-weight: 900; color: #0d1b2e; margin-top: 4px; }
    .meta { display: flex; justify-content: space-between; margin-bottom: 18px; font-size: 11.5px; }
    .meta-block { display: flex; flex-direction: column; gap: 3px; }
    .meta-label { font-weight: 700; color: #64748b; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; }
    .meta-val { color: #1e293b; font-weight: 600; }
    .section-title { font-size: 10.5px; font-weight: 700; color: #64748b; text-transform: uppercase;
      letter-spacing: 0.5px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px; margin-bottom: 10px; }
    .transfer-arrow { display: flex; align-items: center; gap: 14px; margin-bottom: 18px;
      background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px 18px; }
    .wh-box { flex: 1; }
    .wh-label { font-size: 10px; font-weight: 700; color: #94a3b8; text-transform: uppercase; margin-bottom: 3px; }
    .wh-name-from { font-size: 14px; font-weight: 800; color: #c2410c; }
    .wh-name-to   { font-size: 14px; font-weight: 800; color: #166534; }
    .arr { font-size: 22px; color: #7c3aed; font-weight: 900; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
    th { background: #0d1b2e; color: #e2e8f0; padding: 7px 10px; text-align: left; font-size: 11px; letter-spacing: 0.3px; }
    td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; font-size: 12px; }
    tr:nth-child(even) td { background: #f8fafc; }
    .qty-cell { text-align: right; font-weight: 700; font-size: 13px; color: #7c3aed; }
    .obs-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px;
      padding: 10px 14px; font-size: 12px; color: #475569; margin-bottom: 18px; min-height: 36px; }
    .firmas { display: flex; gap: 40px; margin-top: 32px; }
    .firma-box { flex: 1; border-top: 1px solid #94a3b8; padding-top: 8px; text-align: center; }
    .firma-label { font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
    .footer { margin-top: 20px; text-align: center; font-size: 10px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 10px; }
  </style>
</head>
<body>
  <div class="header">
    <div class="co-name">${businessName}</div>
    <div class="doc-title">Comprobante de Transferencia</div>
    <div class="doc-number">${t.transfer_number}</div>
  </div>

  <div class="meta">
    <div class="meta-block">
      <span class="meta-label">Fecha</span>
      <span class="meta-val">${fecha}</span>
    </div>
    <div class="meta-block">
      <span class="meta-label">Usuario</span>
      <span class="meta-val">${t.username || '—'}</span>
    </div>
  </div>

  <div class="section-title">Ruta de transferencia</div>
  <div class="transfer-arrow">
    <div class="wh-box">
      <div class="wh-label">Almacén Origen</div>
      <div class="wh-name-from">${t.from_warehouse_name}</div>
    </div>
    <div class="arr">→</div>
    <div class="wh-box">
      <div class="wh-label">Almacén Destino</div>
      <div class="wh-name-to">${t.to_warehouse_name}</div>
    </div>
  </div>

  <div class="section-title">Detalle del producto</div>
  <table>
    <thead>
      <tr>
        <th>Cód.</th>
        <th>Producto</th>
        <th>U.M</th>
        <th style="text-align:right">Cantidad</th>
        ${especial ? '<th style="text-align:right">Unidades</th>' : ''}
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>${t.sku}</td>
        <td>${t.product_name}</td>
        <td>${t.unit_measure || '—'}</td>
        <td class="qty-cell">${fmtQ(t.quantity)}</td>
        ${especial ? `<td class="qty-cell">${t.qty_unidad}</td>` : ''}
      </tr>
    </tbody>
  </table>

  <div class="section-title">Observaciones</div>
  <div class="obs-box">${t.notes || 'Sin observaciones'}</div>

  <div class="firmas">
    <div class="firma-box"><div class="firma-label">Entregado por</div></div>
    <div class="firma-box"><div class="firma-label">Recibido por</div></div>
    <div class="firma-box"><div class="firma-label">Responsable</div></div>
  </div>

  <div class="footer">
    PIVASAPOLLOS — Generado ${new Date().toLocaleString('es-PE', { timeZone: 'America/Lima' })} — ${t.transfer_number}
  </div>

  <script>window.onload = () => { window.print(); }</script>
</body>
</html>`);
  win.document.close();
}

/* ── Paginación ───────────────────────────────────────────────────────── */
function Paginacion({ page, totalPages, perPage, setPage, setPerPage }) {
  return (
    <div className="mt-pagination">
      <span className="pg-info">({page} de {totalPages})</span>
      <button onClick={() => setPage(1)} disabled={page === 1}><FiChevronsLeft size={12} /></button>
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}><FiChevronLeft size={12} /></button>
      <span className="pg-current">{page}</span>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}><FiChevronRight size={12} /></button>
      <button onClick={() => setPage(totalPages)} disabled={page === totalPages}><FiChevronsRight size={12} /></button>
      <select value={perPage} onChange={e => { setPerPage(+e.target.value); setPage(1); }}>
        {ITEMS.map(n => <option key={n} value={n}>{n}</option>)}
      </select>
    </div>
  );
}

/* ── Modal Nueva Transferencia ───────────────────────────────────────── */
function ModalNueva({ businessId, warehouses, onClose, onSaved }) {
  const [fromWh, setFromWh]       = useState(warehouses[0]?.id || '');
  const [toWh, setToWh]           = useState('');
  const [prodSearch, setProdSearch] = useState('');
  const [products, setProducts]   = useState([]);
  const [product, setProduct]     = useState(null);
  const [cantidad, setCantidad]   = useState('');
  const [unidades, setUnidades]   = useState('');
  const [notas, setNotas]         = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  const destinos = warehouses.filter(w => String(w.id) !== String(fromWh));

  useEffect(() => {
    setToWh(destinos[0]?.id || '');
  }, [fromWh]); // eslint-disable-line

  const loadProducts = useCallback(async () => {
    if (!fromWh) { setProducts([]); return; }
    try {
      const { data } = await getWarehouseStock(fromWh, businessId, { search: prodSearch });
      setProducts(data?.data || []);
    } catch { setProducts([]); }
  }, [fromWh, businessId, prodSearch]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  useEffect(() => {
    setProduct(null);
    setProdSearch('');
    setCantidad('');
    setUnidades('');
  }, [fromWh]);

  const filteredProds = useMemo(() => {
    const q = prodSearch.toLowerCase();
    return products.filter(p =>
      p.name?.toLowerCase().includes(q) ||
      p.sku?.toLowerCase().includes(q)
    );
  }, [products, prodSearch]);

  const especial  = !!product?.special_condition;
  const stockDisp = product ? fmtQ(product.stock) : null;
  const uniDisp   = product ? parseInt(product.stock_unidad || 0) : null;

  async function handleSave() {
    setError('');
    if (!fromWh || !toWh) { setError('Selecciona almacén origen y destino.'); return; }
    if (String(fromWh) === String(toWh)) { setError('El almacén origen y destino no pueden ser el mismo.'); return; }
    if (!product) { setError('Selecciona el producto a transferir.'); return; }

    const qty = parseFloat(cantidad || 0);
    const uni = parseInt(unidades || 0);

    if (qty <= 0) { setError('La cantidad debe ser mayor a 0.'); return; }
    if (especial && uni < 0) { setError('Las unidades no pueden ser negativas.'); return; }

    setSaving(true);
    try {
      const res = await createTransfer(businessId, {
        from_warehouse_id: fromWh,
        to_warehouse_id:   toWh,
        product_id:        product.product_id || product.id,
        quantity:          qty,
        qty_unidad:        especial ? uni : 0,
        notes:             notas.trim() || null,
      });
      onSaved(res.data.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-modal-overlay">
      <div className="mt-modal trf-modal">
        <div className="mt-modal-header">
          <span><FiRepeat size={14} style={{ marginRight: 6 }} />Nueva Transferencia</span>
          <button className="btn-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="mt-modal-body">

          {/* Almacenes */}
          <div className="trf-section-title">Almacenes</div>
          <div className="trf-warehouse-row">
            <div className="trf-field">
              <label><FiArchive size={11} style={{ marginRight: 4 }} />Origen <span className="req">*</span></label>
              <select value={fromWh} onChange={e => setFromWh(e.target.value)}>
                {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
            <div className="trf-wh-arrow-center"><FiArrowRight size={20} /></div>
            <div className="trf-field">
              <label><FiArchive size={11} style={{ marginRight: 4 }} />Destino <span className="req">*</span></label>
              <select value={toWh} onChange={e => setToWh(e.target.value)}>
                <option value="">Seleccione...</option>
                {destinos.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
          </div>

          {/* Producto */}
          <div className="trf-section-title" style={{ marginTop: 16 }}>Producto</div>
          <div className="trf-product-search">
            <FiSearch size={13} />
            <input
              value={prodSearch}
              onChange={e => setProdSearch(e.target.value)}
              placeholder="Buscar por nombre, SKU o código..."
            />
            {prodSearch && (
              <button className="mov-clear-btn" onClick={() => setProdSearch('')}>
                <FiX size={12} />
              </button>
            )}
          </div>

          <div className="trf-product-list">
            {filteredProds.length === 0
              ? <div className="trf-product-empty">
                  {fromWh ? 'Sin productos disponibles en este almacén' : 'Selecciona almacén origen'}
                </div>
              : filteredProds.map(p => {
                  const pid = p.product_id || p.id;
                  const sel = product && (product.product_id || product.id) === pid;
                  return (
                    <div
                      key={pid}
                      className={`trf-product-item ${sel ? 'selected' : ''}`}
                      onClick={() => { setProduct(p); setCantidad(''); setUnidades(''); }}
                    >
                      <span className="pitem-sku">{p.sku}</span>
                      <span className="pitem-name">{p.name}</span>
                      <span className={`pitem-stk ${parseFloat(p.stock || 0) <= 0 ? 'zero' : ''}`}>
                        {fmtQ(p.stock)} {p.unit_measure}
                      </span>
                    </div>
                  );
                })
            }
          </div>

          {/* Cantidades — solo tras seleccionar producto */}
          {product && (
            <>
              <div className="trf-section-title" style={{ marginTop: 16 }}>Cantidad a transferir</div>
              <div className={especial ? 'trf-qty-row' : ''}>
                <div className="trf-field">
                  <label>Cantidad ({product.unit_measure || 'UOM'}) <span className="req">*</span></label>
                  <input
                    type="number" step="0.001" min="0.001"
                    value={cantidad}
                    onChange={e => setCantidad(e.target.value)}
                    autoFocus
                  />
                  <span className="trf-stock-hint">
                    Disponible: <span>{stockDisp} {product.unit_measure}</span>
                  </span>
                </div>

                {especial && (
                  <div className="trf-field">
                    <label>Unidades</label>
                    <input
                      type="number" step="1" min="0"
                      value={unidades}
                      onChange={e => setUnidades(e.target.value)}
                    />
                    <span className="trf-stock-hint">
                      Disponible: <span>{uniDisp} uds.</span>
                    </span>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Observaciones */}
          <div className="trf-section-title" style={{ marginTop: 16 }}>Observaciones</div>
          <div className="trf-field">
            <textarea
              value={notas}
              onChange={e => setNotas(e.target.value)}
              placeholder="Motivo de la transferencia, notas adicionales..."
            />
          </div>

          {error && <div className="trf-error">{error}</div>}
        </div>

        <div className="mt-modal-footer">
          <button
            type="button"
            className="btn-modificar"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Registrar Transferencia'}
          </button>
          <button type="button" className="btn-salir" onClick={onClose}>✕ Salir</button>
        </div>
      </div>
    </div>
  );
}

/* ── Modal resultado post-guardado ───────────────────────────────────── */
function ModalResultado({ transfer, businessName, onClose, onNueva }) {
  return (
    <div className="mt-modal-overlay">
      <div className="mt-modal trf-result-modal">
        <div className="mt-modal-header">
          <span>Transferencia registrada</span>
          <button className="btn-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mt-modal-body" style={{ textAlign: 'center', padding: '24px 20px' }}>
          <div className="trf-result-icon">✅</div>
          <div className="trf-result-number">{transfer.transfer_number}</div>
          <div className="trf-result-detail">
            <strong>{fmtQ(transfer.quantity)} {transfer.unit_measure}</strong> de <strong>{transfer.product_name}</strong><br />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
              <span className="trf-wh-chip from">{transfer.from_warehouse_name}</span>
              <FiArrowRight size={12} />
              <span className="trf-wh-chip to">{transfer.to_warehouse_name}</span>
            </span>
          </div>
          <div className="trf-result-actions">
            <button
              className="btn-trf-imprimir"
              onClick={() => imprimirTransferencia(transfer, businessName)}
            >
              <FiPrinter size={14} /> Imprimir comprobante
            </button>
            <button className="btn-trf-cerrar" onClick={onNueva}>
              <FiPlus size={13} /> Nueva transferencia
            </button>
            <button className="btn-trf-cerrar" onClick={onClose}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Componente principal ─────────────────────────────────────────────── */
function TransferenciasMaintenance({ businessId }) {
  const { user } = useAuth();
  const businessName = user?.business_name || 'PIVASAPOLLOS';

  const [data, setData]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(false);
  const [warehouses, setWH]     = useState([]);
  const [page, setPage]         = useState(1);
  const [perPage, setPerPage]   = useState(10);
  const [modal, setModal]       = useState(false);
  const [resultado, setResultado] = useState(null);

  // Filtros
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate]     = useState('');
  const [fromWh, setFromWh]     = useState('');
  const [toWh, setToWh]         = useState('');
  const [search, setSearch]     = useState('');

  useEffect(() => {
    getWarehouses(businessId)
      .then(r => setWH(r.data?.data || []))
      .catch(() => setWH([]));
  }, [businessId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTransfers(businessId, {
        from_date: fromDate || undefined,
        to_date:   toDate   || undefined,
        from_warehouse_id: fromWh   || undefined,
        to_warehouse_id:   toWh     || undefined,
        search:    search   || undefined,
        page, limit: perPage,
      });
      setData(res.data?.data  || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      alert('Error al cargar transferencias: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }, [businessId, fromDate, toDate, fromWh, toWh, search, page, perPage]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  function whName(id) {
    return warehouses.find(w => String(w.id) === String(id))?.name || id;
  }

  async function handleAnular(row) {
    if (!window.confirm(`¿Anular la transferencia ${row.transfer_number}?\nSe revertirá el stock en ambos almacenes. El registro quedará como ANULADO.`)) return;
    try {
      await anularTransfer(row.id, businessId);
      load();
    } catch (e) {
      alert('Error al anular: ' + (e.response?.data?.error || e.message));
    }
  }

  function handleSaved(transfer) {
    setModal(false);
    setResultado(transfer);
    load();
  }

  function handleNueva() {
    setResultado(null);
    setModal(true);
  }

  return (
    <div className="mt-container">
      {/* Toolbar */}
      <div className="mt-toolbar">
        <div className="mt-toolbar-left">
          <button className="btn-nuevo" onClick={() => setModal(true)}>
            <FiPlus size={12} /> Nueva Transferencia
          </button>
        </div>
        <div className="mt-search-global">
          <span>Búsqueda</span>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Producto, SKU, N° transferencia..."
          />
        </div>
      </div>

      {/* Filtros */}
      <div className="trf-filters">
        <div className="trf-filter-group">
          <label>Desde</label>
          <input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setPage(1); }} />
        </div>
        <div className="trf-filter-group">
          <label>Hasta</label>
          <input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setPage(1); }} />
        </div>
        <div className="trf-filter-group">
          <label>Almacén Origen</label>
          <select value={fromWh} onChange={e => { setFromWh(e.target.value); setPage(1); }}>
            <option value="">Todos</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
        <div className="trf-filter-group">
          <label>Almacén Destino</label>
          <select value={toWh} onChange={e => { setToWh(e.target.value); setPage(1); }}>
            <option value="">Todos</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
        <div className="trf-filter-group" style={{ marginLeft: 'auto' }}>
          <label>&nbsp;</label>
          <button
            style={{ padding: '6px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0',
              borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#475569' }}
            onClick={() => { setFromDate(''); setToDate(''); setFromWh(''); setToWh(''); setSearch(''); setPage(1); }}
          >
            Limpiar
          </button>
        </div>
      </div>

      {/* Resumen */}
      <div className="trf-summary-bar">
        <FiRepeat size={13} />
        <span>Total transferencias:</span>
        <span className="trf-summary-pill">{total}</span>
      </div>

      {/* Paginación superior */}
      <div className="mt-export-bar">
        <Paginacion
          page={page} totalPages={totalPages}
          perPage={perPage} setPage={setPage} setPerPage={setPerPage}
        />
      </div>

      {/* Tabla */}
      <div className="mt-table-wrap">
        {loading
          ? <div className="mt-loading">Cargando...</div>
          : (
            <table className="mt-table mt-table-fixed">
              <colgroup>
                <col style={{ width: '11%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '27%' }} />
                <col style={{ width: '9%'  }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '9%'  }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '7%'  }} />
              </colgroup>
              <thead>
                <tr className="mt-head-row">
                  <th>N° Transfer.</th>
                  <th>Fecha</th>
                  <th>Origen → Destino</th>
                  <th>SKU</th>
                  <th>Producto</th>
                  <th>Cantidad</th>
                  <th>Acción</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0
                  ? <tr className="mt-empty"><td colSpan={8}>Sin transferencias registradas</td></tr>
                  : data.map((row, i) => {
                    const anulado = !!row.anulado;
                    const rowCls  = anulado ? 'mt-row-anulado' : (i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd');
                    return (
                      <tr key={row.id} className={rowCls}>
                        <td className="no-strike">
                          <span className="trf-badge trf-badge-transfer">{row.transfer_number}</span>
                        </td>
                        <td style={{ fontSize: 11.5 }}>{fmtDt(row.created_at)}</td>
                        <td>
                          <span className="trf-arrow">
                            <span className="trf-wh-chip from">{row.from_warehouse_name}</span>
                            <FiArrowRight size={11} className="trf-arrow-icon" />
                            <span className="trf-wh-chip to">{row.to_warehouse_name}</span>
                          </span>
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: 11.5 }}>{row.sku}</td>
                        <td className="td-truncate" title={row.product_name}>{row.product_name}</td>
                        <td style={{ fontWeight: 700, color: anulado ? '#94a3b8' : '#7c3aed' }}>
                          {fmtQ(row.quantity)} {row.unit_measure}
                          {row.special_condition && row.qty_unidad > 0 &&
                            <span style={{ fontSize: 10.5, marginLeft: 4 }}>/ {row.qty_unidad} uds</span>
                          }
                        </td>
                        <td className="no-strike" style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                          <button
                            className="btn-trf-print"
                            disabled={anulado}
                            onClick={() => !anulado && imprimirTransferencia(row, businessName)}
                            title={anulado ? 'Transferencia anulada' : 'Imprimir comprobante'}
                          >
                            <FiPrinter size={11} /> Imprimir
                          </button>
                          <button
                            className="btn-row-del"
                            disabled={anulado}
                            onClick={() => !anulado && handleAnular(row)}
                            title={anulado ? 'Ya anulada' : 'Anular transferencia'}
                          >
                            <FiX size={13} />
                          </button>
                        </td>
                        <td className="no-strike">
                          {anulado
                            ? <span className="badge-estado-anulado">ANULADO</span>
                            : <span className="badge-estado-activo">ACTIVO</span>
                          }
                        </td>
                      </tr>
                    );
                  })
                }
              </tbody>
            </table>
          )
        }
      </div>

      {/* Paginación inferior */}
      <div className="mt-export-bar">
        <Paginacion
          page={page} totalPages={totalPages}
          perPage={perPage} setPage={setPage} setPerPage={setPerPage}
        />
      </div>

      {/* Modal nueva transferencia */}
      {modal && (
        <ModalNueva
          businessId={businessId}
          warehouses={warehouses}
          onClose={() => setModal(false)}
          onSaved={handleSaved}
        />
      )}

      {/* Modal resultado con opción de imprimir */}
      {resultado && (
        <ModalResultado
          transfer={resultado}
          businessName={businessName}
          onClose={() => setResultado(null)}
          onNueva={handleNueva}
        />
      )}
    </div>
  );
}

export default TransferenciasMaintenance;
